/* eslint-disable complexity */
import '../../../../../packages/neb-lit-components/src/components/patients/neb-patient-summary-controller';

import { openPopup } from '@neb/popup';
import { html, css } from 'lit';

import {
  createBannerController,
  DISPLAY_ON,
} from '../../../../../packages/neb-alert/components/neb-alert-banner-controller';
import {
  billingCheckOutAppointment,
  getCheckOutSummary,
} from '../../../../../packages/neb-api-client/src/appointment-api-client';
import {
  CHANGE_UNITS_PROMPT_UNALLOCATION,
  UNPOST_PROMPT_UNALLOCATION,
} from '../../../../../packages/neb-api-client/src/services/encounter-charge/check-unallocation-authorization';
import { openInfo } from '../../../../../packages/neb-dialog/neb-banner-state';
import Overlay from '../../../../../packages/neb-lit-components/src/components/overlays/neb-overlay';
import { APPOINTMENT_ACTIONS } from '../../../../../packages/neb-lit-components/src/components/scheduling/neb-appointment-options';
import '../../../../../packages/neb-lit-components/src/components/neb-popup-header';
import { POPUP_RENDER_KEYS } from '../../../../../packages/neb-popup/src/renderer-keys';
import { store } from '../../../../../packages/neb-redux/neb-redux-store';
import { parseDate } from '../../../../../packages/neb-utils/date-util';
import { UpdateNotificationService } from '../../../../services/update-notifications';
import { openError, openSuccess } from '../../../../store';
import { CSS_SPACING, CSS_COLOR_GREY_2 } from '../../../../styles';
import { REMOVE_ALLOCATION_CONFIRMATION_POPUP } from '../../../../utils/check-in-out';
import { ANY } from '../../../../utils/update-notifications';
import {
  PATIENT_INSURANCE_VISIT_LIMIT_EXCEEDED,
  PATIENT_INSURANCE_VISIT_LIMIT_NEAR_OR_AT_LIMIT,
  WARNING_MULTIPLE_INVOICES_ASSOCIATED_AT_APPOINTMENT_LEVEL,
  WARNING_SINGLE_INVOICE_ASSOCIATED_AT_APPOINTMENT_LEVEL,
} from '../../../../utils/user-message';
import { getDefaultInsurances } from '../../utils/neb-form-check-in-util';

import { NebFormCheckOut } from './neb-form-check-out';

export const ELEMENTS = {
  patientSummary: {
    id: 'patient-summary',
  },
  header: {
    id: 'header',
  },
  formCheckOut: {
    id: 'form-check-out',
  },
};

const ASSUMED_VISITS_TODAY = 1;

export const WARNING_TYPE = {
  ASSOCIATED_INVOICES: 'associated-invoices',
  MODIFIED_ALLOCATED_CHARGES: 'modified-allocated-charges',
  BILL_TYPE_CHANGE_ALLOCATED_CHARGES: 'bill-type-change-allocated-charges',
};
const SAVE_ONLY_ERROR_MESSAGE =
  'An error occurred when saving check out details';

const SAVE_ONLY_SUCCESS_MESSAGE = 'Check out details saved sucessfully';
class NebOverlayCheckOut extends Overlay {
  static get properties() {
    return {
      formModel: Object,
      formMetaData: Object,
      __savingError: Boolean,
      __defaultInsurances: Object,
    };
  }

  initState() {
    super.initState();

    this.formModel = NebFormCheckOut.createModel();
    this.formMetaData = NebFormCheckOut.createMetaData();

    this.__savingError = false;

    this.__defaultInsurances = null;

    this.__checkOutBanner = createBannerController(DISPLAY_ON.checkOut);

    this.__notificationService = new UpdateNotificationService({
      defaultQuery: {
        patientCase: ANY,
      },
      callback: () => {
        this.__refreshMetaDataModel();
      },
    });
  }

  initHandlers() {
    super.initHandlers();
    this.handlers = {
      ...this.handlers,
      save: async saveModel => {
        const { isSaveOnly } = saveModel;

        let checkOutRes;

        try {
          checkOutRes = await billingCheckOutAppointment(
            saveModel.id,
            saveModel,
          );

          if (Array.isArray(checkOutRes)) {
            checkOutRes = await this.__handleSaveWarnings(
              checkOutRes,
              saveModel,
            );

            if (!checkOutRes) return;
          }

          if (checkOutRes.validationError) {
            store.dispatch(openError(checkOutRes.validationError));

            return;
          }
        } catch (e) {
          console.error(e);

          store.dispatch(
            openError(
              isSaveOnly
                ? SAVE_ONLY_ERROR_MESSAGE
                : APPOINTMENT_ACTIONS.CHECK_OUT.errorMessage,
            ),
          );

          return;
        }

        store.dispatch(
          openSuccess(
            isSaveOnly
              ? SAVE_ONLY_SUCCESS_MESSAGE
              : APPOINTMENT_ACTIONS.CHECK_OUT.successMessage,
          ),
        );

        if (isSaveOnly) {
          await this.__refresh();
          return;
        }

        this.isDirty = false;
        this.dismiss({
          success: true,
          model: checkOutRes.data[0],
          cancelled: false,
          mode: 'checkOut',
        });
      },
      cancel: () =>
        this.dismiss({
          success: true,
          model: { id: this.formMetaData.appointment.id || '' },
          cancelled: true,
        }),
      refresh: async () => {
        await this.__refresh();
      },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .content {
          flex-flow: row nowrap;
          width: 100%;
        }

        .header {
          padding: ${CSS_SPACING};
        }

        .content-left {
          padding: ${CSS_SPACING};
          border-right: 1px solid ${CSS_COLOR_GREY_2};
        }

        .content-right {
          display: flex;
          flex-flow: column nowrap;
          width: 100%;
        }

        .form {
          flex: 1 0 0;
        }
      `,
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    this.__checkOutBanner.connect();
    this.__notificationService.connect();
  }

  disconnectedCallback() {
    this.__checkOutBanner.disconnect();
    this.__notificationService.disconnect();
    super.disconnectedCallback();
  }

  async __handleSaveWarnings(checkOutRes, saveModel) {
    const modifiedAllocationWarning = checkOutRes.find(
      ({ type }) => type === WARNING_TYPE.MODIFIED_ALLOCATED_CHARGES,
    );

    const associatedInvoicesWarning = checkOutRes.find(
      ({ type }) => type === WARNING_TYPE.ASSOCIATED_INVOICES,
    );

    const billTypeChangeWithAllocatedChargesWarning = checkOutRes.find(
      ({ type }) => type === WARNING_TYPE.BILL_TYPE_CHANGE_ALLOCATED_CHARGES,
    );

    if (modifiedAllocationWarning) {
      const result = modifiedAllocationWarning.chargesToBeUnpostedHasAllocations
        ? await openPopup(POPUP_RENDER_KEYS.CONFIRM, UNPOST_PROMPT_UNALLOCATION)
        : await openPopup(
            POPUP_RENDER_KEYS.CONFIRM,
            CHANGE_UNITS_PROMPT_UNALLOCATION,
          );

      if (!result) return null;
    }

    let associatedInvoicesWarningMessage;

    if (associatedInvoicesWarning) {
      associatedInvoicesWarningMessage =
        associatedInvoicesWarning.associatedInvoiceNumbers.length === 1
          ? WARNING_SINGLE_INVOICE_ASSOCIATED_AT_APPOINTMENT_LEVEL(
              associatedInvoicesWarning.associatedInvoiceNumbers[0],
            )
          : WARNING_MULTIPLE_INVOICES_ASSOCIATED_AT_APPOINTMENT_LEVEL(
              associatedInvoicesWarning.associatedInvoiceNumbers,
            );

      if (!billTypeChangeWithAllocatedChargesWarning) {
        const result = await openPopup(POPUP_RENDER_KEYS.DIALOG, {
          title: 'Warning',
          message: associatedInvoicesWarningMessage,
          buttons: [
            { name: 'save', label: 'YES' },
            { name: 'discard', label: 'NO' },
          ],
        });

        if (result === 'discard') return null;
      }
    }

    if (billTypeChangeWithAllocatedChargesWarning) {
      const result = await openPopup(
        POPUP_RENDER_KEYS.CONFIRM,
        REMOVE_ALLOCATION_CONFIRMATION_POPUP(
          associatedInvoicesWarningMessage?.[0],
        ),
      );

      if (!result) return null;
    }

    checkOutRes = await billingCheckOutAppointment(saveModel.id, {
      ...saveModel,
      shouldOverrideWarnings: true,
    });

    return checkOutRes;
  }

  async __refreshMetaDataModel() {
    const { meta } = await getCheckOutSummary(this.formMetaData.appointment.id);

    this.formMetaData = meta;
  }

  async __refresh() {
    const { form, meta } = await getCheckOutSummary(
      this.formMetaData.appointment.id,
    );

    this.__setFormData(form, meta);
  }

  __setFormData(form, meta) {
    this.formModel = form;
    this.formMetaData = meta;

    this.__defaultInsurances = getDefaultInsurances(
      this.formMetaData.insurances,
    );
  }

  __calculateIfInsuranceHasRemainingVisits(patientInsuranceVisitLimit) {
    return (
      patientInsuranceVisitLimit &&
      patientInsuranceVisitLimit.maxVisits !== '' &&
      patientInsuranceVisitLimit.totalRemaining !== null
    );
  }

  __handlePatientInsuranceVisitLimit() {
    if (this.formMetaData.patientPrimaryInsuranceVisitLimit) {
      if (
        this.__calculateIfInsuranceHasRemainingVisits(
          this.formMetaData.patientPrimaryInsuranceVisitLimit,
        )
      ) {
        const { maxVisits, totalRemaining, asOf } =
          this.formMetaData.patientPrimaryInsuranceVisitLimit;

        if (asOf) {
          const today = parseDate().startOf('day');
          const momentAsOf = parseDate(asOf);
          if (momentAsOf.isAfter(today)) return;
        }

        const visitsRemaining = totalRemaining - ASSUMED_VISITS_TODAY;

        this.__dispatchVisitLimitInfoBanner(visitsRemaining, maxVisits);
      }
    }
  }

  __dispatchVisitLimitInfoBanner(visitsRemaining, maxVisits) {
    if (visitsRemaining <= 2 && visitsRemaining >= 0) {
      store.dispatch(
        openInfo(
          PATIENT_INSURANCE_VISIT_LIMIT_NEAR_OR_AT_LIMIT(
            visitsRemaining,
            maxVisits,
          ),
        ),
      );
    }

    if (visitsRemaining === -1 || visitsRemaining === -2) {
      store.dispatch(openInfo(PATIENT_INSURANCE_VISIT_LIMIT_EXCEEDED));
    }
  }

  updated(changedProps) {
    if (changedProps.has('model') && this.model.meta?.appointment?.id) {
      const { form, meta } = this.model;
      this.__setFormData(form, meta);
      this.__handlePatientInsuranceVisitLimit();

      this.__checkOutBanner.update(
        this.model.form.appointmentDetails.patientId,
      );
    }
  }

  renderContent() {
    return html`
      ${this.layout !== 'small'
        ? html`
            <neb-patient-summary-controller
              id="${ELEMENTS.patientSummary.id}"
              class="content-left"
              .patientId="${this.model.form.appointmentDetails.patientId}"
              .showTreatmentPlanLink="${true}"
              .initialModel="${this.model.patientSummaryModel}"
            >
            </neb-patient-summary-controller>
          `
        : ''}

      <div class="content-right">
        <neb-popup-header
          id="${ELEMENTS.header.id}"
          class="header"
          title="Check Out"
          .onBack="${this.handlers.cancel}"
          .onCancel="${this.handlers.cancel}"
          .showBackButton="${this.layout === 'small'}"
          .showCancelButton="${this.layout !== 'small'}"
        ></neb-popup-header>

        <neb-form-check-out
          id="${ELEMENTS.formCheckOut.id}"
          class="form"
          .layout="${this.layout}"
          .model="${this.formModel}"
          .metaData="${this.formMetaData}"
          .savingError="${this.__savingError}"
          .defaultInsurances="${this.__defaultInsurances}"
          .onCancel="${this.handlers.cancel}"
          .onChangeDirty="${this.handlers.dirty}"
          .onSave="${this.handlers.save}"
          .onRefresh="${this.handlers.refresh}"
        ></neb-form-check-out>
      </div>
    `;
  }
}

customElements.define('neb-overlay-check-out', NebOverlayCheckOut);
